import ApiClient from "./ApiClient"
import ServiceError from "./ServiceError"
import { getDate, compareDates } from "./serviceUtils"

export const EventBookingState = {
  FULL: "FULL",
  NEARLY_FULL: "NEARLY_FULL",
  FREE: "FREE",
  UNKNOWN: "UNKNOWN",
}

export const ApplicationState = {
  NO_APPLICATION_PRESENT: "NO_APPLICATION_PRESENT",
  IN_PROGRESS: "IN_PROGRESS",
  UPDATE_REQUIRED: "UPDATE_REQUIRED",
  IN_REVIEW: "IN_REVIEW",
  APPROVED: "APPROVED",
  DENIED: "DENIED",
}

export const ApplicationsAndEventsStatus = {
  noDfbNetLink: "noDfbNetLink",
  oneApplication: "oneApplication",
  severalApplications: "severalApplications",
  noApplicationsOneEvent: "noApplicationsOneEvent",
  noApplicationsSeveralEvents: "noApplicationsSeveralEvents",
  noApplicationsNoEvents: "noApplicationsNoEvents",
}

const getApiClient = () =>
  new ApiClient(
    "dfbNet",
    `${process.env.PERSONALISATION_HOST}/api/user/dfbNet/`
  )

export async function getDfbAccount() {
  return getApiClient().get("account")
}

export async function deleteDfbAccount() {
  return getApiClient().delete("account")
}

/*
 "currentApplicationsRestResponse": {
    "validDfbNetAccountLink": true,
    "currentApplications": [
      {
        "educationId": "00FO7VGASK000000VV0AG103VUJ3A8BC",
        "educationLabel": "Fussball-Lehrer",
        "status": {
          "key": "DENIED",
          "label": "Abgelehnt"
        },
        "date": "2020-10-04",
        "editApplicationUrl": "https://mein.intg-dfb-akademie.de/personalisierung/candidatureButtonRedirect?educationUuid=00FO7VGASK000000VV0AG103VUJ3A8BC"
      }
    ]
  },
 */
const mapApplications = applications => {
  return applications.map(item => ({
    ...item,
    date: getDate(item.date),
  }))
}

/*
  "eventListRestResponse": {
    "validDfbNetAccountLink": true,
    "licenseDisplayName": "Trainer-A",
    "sections": [
      {
        "sectionId": "00RJVH067G000000VV0AG80NVV4EQM0U",
        "sectionName": "A-Lizenz",
        "categories": [
          {
            "categoryId": "0289MIJ4SS000000VS5489B5VSV3137N",
            "categoryName": "Ausbildung 2020",
            "events": [
              {
                "id": "028AL47DB0000000VS5489B5VSV3137N",
                "name": "2020 A-Lizenz-Ausbildung Kaiserau 3",
                "begin": "2020-12-13T23:00:00.000+0000",
                "end": "2021-01-27T23:00:00.000+0000",
                "eventBookingState": {
                  "key": "NEARLY_FULL",
                  "label": "wenige Plätze vorhanden"
                }
              }
            ]
          }
        ]
      }
    ]
  }
 */
const mapEvents = sections => {
  const result = []
  sections.forEach(section => {
    section.categories.forEach(category => {
      category.events.forEach(event => {
        result.push({
          ...event,
          begin: getDate(event.begin),
          end: getDate(event.end),
          categoryName: category.categoryName,
          sectionName: section.sectionName,
        })
      })
    })
  })
  result.sort((a, b) => {
    const startComp = compareDates(a.start, b.start)
    if (startComp === 0) {
      return compareDates(a.end, b.end)
    }
    return startComp
  })
  return result
}

const applicationsAndEventsDataMapper = data => {
  if (
    !data.currentApplicationsRestResponse ||
    !data.eventListRestResponse
  ) {
    console.error("Unexpected application and events response", data)
    throw new ServiceError(
      ServiceError.STATUS_INVALID_DATA,
      "dfbNet service got unexpected data"
    )
  }

  if (
    data.currentApplicationsRestResponse.validDfbNetAccountLink !== true ||
    data.eventListRestResponse.validDfbNetAccountLink !== true
  ) {
    throw new ServiceError(
      ServiceError.STATUS_FORBIDDEN,
      "User has no DFBnet connection"
    )
  }

  const applications =
    data.currentApplicationsRestResponse.currentApplications || []
  const eventSections = data.eventListRestResponse.sections || []

  return {
    applications: mapApplications(applications),
    events: mapEvents(eventSections),
    licenseDisplayName: data.eventListRestResponse.licenseDisplayName,
  }
}

/**
 * Will return a flattened list with "applications" and "events"
 * from the currentApplicationsAndEvents API call.
 * The nested structure is removed and dates are converted to Date objects.
 * @return {Promise<*>}
 */
export async function getApplicationsAndEvents() {
  return getApiClient().get(
    "currentApplicationsAndEvents",
    applicationsAndEventsDataMapper
  )
}

export async function getApplicationsAndEventsStatus() {
  try {
    const data = await getApplicationsAndEvents()
    const notDenied = data.applications.filter(
      application => application.status.key !== ApplicationState.DENIED
    )
    if (notDenied.length > 0) {
      return {
        status:
          notDenied.length === 1
            ? ApplicationsAndEventsStatus.oneApplication
            : ApplicationsAndEventsStatus.severalApplications,
        openApplicationsCount: notDenied.length,
      }
    }
    if (data.events.length > 0) {
      return {
        status:
          data.events.length === 1
            ? ApplicationsAndEventsStatus.noApplicationsOneEvent
            : ApplicationsAndEventsStatus.noApplicationsSeveralEvents,
        eventsCount: data.events.length,
      }
    }
    return {
      status: ApplicationsAndEventsStatus.noApplicationsNoEvents,
    }
  } catch (err) {
    if (err.statusCode === ServiceError.STATUS_FORBIDDEN) {
      return {
        status: ApplicationsAndEventsStatus.noDfbNetLink,
      }
    }
    throw err
  }
}
